import { Button, CloseIcon, Notification, SendIcon } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { MouseEventHandler, useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import styled, { css } from 'styled-components'

import { DetailList, MainTitle, OrganisationExpander, ViewLoading } from '../components'
import { StoreContext } from '../store'
import { ContentSection } from '../style/layout'
import { formatDate, getInviteState } from '../utils'
import { breakpoint } from '../utils/breakpoint'

const InviteDetailsViewComponent: React.FC = () => {
  const [t] = useTranslation()
  const { inviteDetailsStore } = useContext(StoreContext)
  const { id: inviteId } = useParams()
  const {
    isLoading,
    inviteDetail,
    fetchingStage,
    selectedRoleValues,
    selectedCustomerNumbers,
    selectedLCNumbers,
    selectedTransportIds,
    selectedBusinessPartnerNumbers,
    selectedPrincipalIds,
  } = inviteDetailsStore

  useEffect(() => {
    inviteDetailsStore.fetchInviteData(inviteId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleResend: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      return inviteDetailsStore.resendInvite(event.currentTarget.value)
    },
    [inviteDetailsStore]
  )

  const handleCancel: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      return inviteDetailsStore.cancelInvite(event.currentTarget.value)
    },
    [inviteDetailsStore]
  )

  if (isLoading) {
    return <ViewLoading />
  }

  const { email, created, expires, expired, cancelled, accepted } = inviteDetail

  return (
    <ContentSection gap="lg">
      <ContentSection gap="xl">
        <MainTitle>{t(`invite.inviteDetailsTitle`)}</MainTitle>
        <DetailList
          items={[
            { id: 'created', label: t(`general.created`), value: formatDate(created) },
            { id: 'email', label: t(`invite.userUsername`), value: email },
            {
              id: 'valid',
              label: t(`general.valid`),
              value: getInviteState(cancelled, expired, expires, accepted, t),
            },
          ]}
        />
      </ContentSection>
      <ContentSection>
        {fetchingStage && (
          <Notification contentType={getNotiType(fetchingStage)}>
            <span>{getNotiText(fetchingStage, t)}</span>
          </Notification>
        )}

        <StyledOrgExpanderWrapper>
          <OrganisationExpander
            organization={inviteDetail.organization}
            selectedRoleValues={selectedRoleValues}
            selectedCustomerNumbers={selectedCustomerNumbers}
            selectedLCNumbers={selectedLCNumbers}
            selectedTransportIds={selectedTransportIds}
            selectedBusinessPartnerNumbers={selectedBusinessPartnerNumbers}
            selectedPrincipalIds={selectedPrincipalIds}
          />
          <StyledButtonsWrapper>
            <Button
              disabled={Boolean(accepted)}
              id="resend_button"
              value={inviteId}
              onClick={handleResend}
              contentMode="secondary"
              hasBackground={false}
              size="sm"
              leftIcon={SendIcon}
            >
              {t(`general.resend`)}
            </Button>
            <Button
              disabled={Boolean(accepted || cancelled || expired)}
              id="cancel_button"
              value={inviteId}
              onClick={handleCancel}
              contentMode="secondary"
              hasBackground={false}
              size="sm"
              leftIcon={CloseIcon}
            >
              {t(`general.cancel`)}
            </Button>
          </StyledButtonsWrapper>
        </StyledOrgExpanderWrapper>
      </ContentSection>
    </ContentSection>
  )
}

export const InviteDetailsView = observer(InviteDetailsViewComponent)

function getNotiType(fetchingStage: string) {
  switch (fetchingStage) {
    case 'fetching':
      return 'warning'
    case 'resent':
      return 'success'
    case 'cancelled':
      return 'success'
  }
}

function getNotiText(fetchingStage: string, t: (key: string) => string) {
  switch (fetchingStage) {
    case 'fetching':
      return t(`general.fetching`)
    case 'resent':
      return t(`general.sent`)
    case 'cancelled':
      return t(`general.cancelled`)
  }
}

const StyledOrgExpanderWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.space8}rem;
  flex-wrap: nowrap;

  > :first-child {
    @media ${breakpoint.mobile} {
      width: 100%;
      order: 2;
    }
  }

  @media ${breakpoint.mobile} {
    flex-wrap: wrap;
    justify-content: end;
  }
`

const StyledButtonsWrapper = styled('div')(
  ({ theme: { spacing } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing.space4}rem;
    align-items: flex-start;

    @media ${breakpoint.mobile} {
      order: 1;
      flex-direction: row;
      align-items: center;
    }
  `
)
